import React, { useEffect, useState } from 'react';
import { useStore } from '@/state/store';
import { useI18n } from '@/i18n/I18nContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { encryptJSON, isCryptoAvailable } from '@/lib/crypto';
import { ShieldCheck, Eye, EyeOff, Copy, Check, Link2, KeyRound, Lock, Pencil } from 'lucide-react';

const MIN_LEN = 8;

export const ShareDialog = ({ open, onOpenChange }) => {
  const { t } = useI18n();
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mode, setMode] = useState('full');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { link, expiresAt, mode }
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setPwd('');
      setConfirm('');
      setMode('full');
      setShow(false);
      setBusy(false);
      setError('');
      setResult(null);
      setCopied(false);
    }
  }, [open]);

  const create = async () => {
    setError('');
    if (!isCryptoAvailable()) {
      setError(t('crypto_unavailable'));
      return;
    }
    if (pwd.length < MIN_LEN) {
      setError(t('share_password_short'));
      return;
    }
    if (pwd !== confirm) {
      setError(t('share_password_mismatch'));
      return;
    }
    setBusy(true);
    try {
      const { doc } = useStore.getState();
      const payload = await encryptJSON({ kind: 'switchr-share', mode, doc }, pwd);
      const res = await api.post('/shares', payload);
      const link = `${window.location.origin}${window.location.pathname}?p=${res.data.id}`;
      try {
        await navigator.clipboard.writeText(link);
        setCopied(true);
      } catch (e) {}
      setResult({ link, expiresAt: res.data.expiresAt, mode });
      toast.success(t('toast_share_ok'));
    } catch (e) {
      setError(t('toast_share_err'));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {}
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#0F7B6C]" /> {t('share_dialog_title')}
          </DialogTitle>
          <DialogDescription>{t('share_dialog_desc')}</DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('share_mode_label')}</Label>
              <RadioGroup value={mode} onValueChange={setMode} className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <label
                  htmlFor="share-mode-full"
                  className={`flex cursor-pointer items-start gap-2 rounded-md border p-3 transition-colors ${mode === 'full' ? 'border-[#0B5FFF] bg-[#0B5FFF]/[0.06]' : 'border-border hover:bg-accent'}`}
                  data-testid="share-mode-full"
                >
                  <RadioGroupItem value="full" id="share-mode-full" className="mt-0.5" />
                  <span className="space-y-0.5">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-[#111418]">
                      <Pencil className="h-3.5 w-3.5" /> {t('share_mode_full')}
                    </span>
                    <span className="block text-xs text-muted-foreground">{t('share_mode_full_desc')}</span>
                  </span>
                </label>
                <label
                  htmlFor="share-mode-view"
                  className={`flex cursor-pointer items-start gap-2 rounded-md border p-3 transition-colors ${mode === 'view' ? 'border-[#0B5FFF] bg-[#0B5FFF]/[0.06]' : 'border-border hover:bg-accent'}`}
                  data-testid="share-mode-view"
                >
                  <RadioGroupItem value="view" id="share-mode-view" className="mt-0.5" />
                  <span className="space-y-0.5">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-[#111418]">
                      <Eye className="h-3.5 w-3.5" /> {t('share_mode_view')}
                    </span>
                    <span className="block text-xs text-muted-foreground">{t('share_mode_view_desc')}</span>
                  </span>
                </label>
              </RadioGroup>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('share_password')}</Label>
              <div className="relative">
                <KeyRound className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type={show ? 'text' : 'password'}
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  className="h-9 pl-8 pr-9"
                  placeholder={t('share_password_hint')}
                  data-testid="share-password-input"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={show ? 'hide' : 'show'}
                  data-testid="share-password-toggle"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('share_password_confirm')}</Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type={show ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && create()}
                  className="h-9 pl-8"
                  placeholder={t('share_password_confirm')}
                  data-testid="share-password-confirm-input"
                />
              </div>
            </div>

            {error && (
              <p className="text-sm text-[#C2410C]" data-testid="share-error">
                {error}
              </p>
            )}

            <div className="rounded-md border border-border bg-[#F4F5F3] p-3 text-xs leading-relaxed text-muted-foreground">
              <div className="mb-1 flex items-center gap-1.5 font-medium text-[#111418]">
                <ShieldCheck className="h-3.5 w-3.5 text-[#0F7B6C]" /> {t('share_notice_title')}
              </div>
              <ul className="list-disc space-y-0.5 pl-4">
                <li>{t('share_notice_zk')}</li>
                <li>{t('share_notice_pwd')}</li>
                <li>{t('share_notice_expiry')}</li>
              </ul>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
                {t('cancel')}
              </Button>
              <Button onClick={create} disabled={busy} data-testid="share-create-button">
                <ShieldCheck className="mr-2 h-4 w-4" />
                {busy ? t('share_creating') : t('share_create')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${result.mode === 'view' ? 'bg-[#0F7B6C]/10 text-[#0F7B6C]' : 'bg-[#0B5FFF]/10 text-[#0B5FFF]'}`}
              data-testid="share-result-mode"
            >
              {result.mode === 'view' ? <Eye className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
              {result.mode === 'view' ? t('share_mode_view') : t('share_mode_full')}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('share_link_label')}</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Link2 className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input value={result.link} readOnly className="h-9 pl-8 font-mono text-xs" data-testid="share-link-output" />
                </div>
                <Button variant="outline" onClick={copyLink} data-testid="share-copy-button">
                  {copied ? <Check className="mr-1.5 h-4 w-4 text-[#0F7B6C]" /> : <Copy className="mr-1.5 h-4 w-4" />}
                  {copied ? t('share_copied') : t('share_copy')}
                </Button>
              </div>
            </div>

            <div className="rounded-md border border-[#0F7B6C]/30 bg-[#0F7B6C]/[0.06] p-3 text-xs leading-relaxed text-[#111418]">
              <div className="mb-1 flex items-center gap-1.5 font-medium">
                <KeyRound className="h-3.5 w-3.5 text-[#0F7B6C]" /> {t('share_notice_pwd_title')}
              </div>
              <p>{t('share_notice_pwd_long')}</p>
              <p className="mt-1 text-muted-foreground">{t('share_notice_expiry')}</p>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => onOpenChange(false)} data-testid="share-done-button">
                {t('share_done')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
